// Global SSE Service - Single connection for entire app
import { connectSSE } from './api';
import { perfMark } from '../utils/perfTracker';
import { feLog } from '../utils/frontendLogger';
import { isTokenExpired, getTokenExpiresAt } from '../utils/jwt';

// Auth-related thresholds for SSE reconnection
const QUICK_FAILURE_THRESHOLD = 5;    // Stop retrying after this many consecutive rapid failures
const QUICK_FAILURE_WINDOW_MS = 3000; // Connection shorter than this counts as a quick failure
const TOKEN_REFRESH_TTL_RATIO = 0.8;  // Refresh token when 80% of TTL has elapsed
const MIN_PROACTIVE_REFRESH_MS = 10000; // Minimum delay before proactive refresh

class GlobalSSEService {
    constructor() {
        this.connection = null;
        this.token = null;
        // Convert saved UI tab name to server tab name
        const savedTab = localStorage.getItem('v3_active_tab');
        if (savedTab) {
            const v3TabMapping = {
                'world': 'world_v3',
                'inventory': 'v3_inventory',
                'hero': 'v3_hero',
                'guild': 'v3_guild',
                'feed': 'feed_v3'
            };
            this.activeTab = v3TabMapping[savedTab] || savedTab;
        } else {
            this.activeTab = null;
        }
        this.handlers = new Map(); // tab -> handler function
        this.isConnected = false;
        this.reconnectTimer = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = Infinity; // Always try to reconnect
        this.reconnectDelay = 1000; // Start with 1 second
        this.maxReconnectDelay = 30000; // Max 30 seconds
        this.connectionStateListeners = new Set(); // For UI updates
        this.isPageVisible = true; // Track page visibility
        this.lastEventTime = Date.now(); // Track last event received
        this.heartbeatTimeout = null; // Timer for heartbeat timeout
        this.heartbeatInterval = 90000; // 90 seconds - server should send keepalive every 30-60s
        this.tokenRefresher = null; // async fn that returns new token or null
        this.consecutiveQuickFailures = 0; // Track rapid connection failures (likely auth problem)
        this.proactiveRefreshTimer = null; // Timer for proactive token refresh before expiry
        this.setupVisibilityHandling();
    }

    // Register an async function that refreshes the auth token
    // The function should return the new token string, or null on failure
    setTokenRefresher(fn) {
        this.tokenRefresher = fn;
    }

    // Schedule proactive token refresh at 80% of TTL
    scheduleProactiveRefresh(token) {
        if (this.proactiveRefreshTimer) {
            clearTimeout(this.proactiveRefreshTimer);
            this.proactiveRefreshTimer = null;
        }

        if (!this.tokenRefresher) return;

        const expiresAt = getTokenExpiresAt(token);
        if (!expiresAt) return;

        const now = Date.now();
        const ttl = expiresAt - now;
        if (ttl <= 0) return;

        // Refresh at 80% of TTL
        const refreshIn = Math.max(ttl * TOKEN_REFRESH_TTL_RATIO, MIN_PROACTIVE_REFRESH_MS);
        feLog('info', 'sse_diag.proactive_refresh_scheduled', '', {
            refresh_in_s: String(Math.round(refreshIn / 1000)),
            ttl_s: String(Math.round(ttl / 1000)),
        });

        this.proactiveRefreshTimer = setTimeout(async () => {
            this.proactiveRefreshTimer = null;
            if (!this.tokenRefresher) return;

            feLog('info', 'sse_diag.proactive_refresh_start', '');
            try {
                const newToken = await this.tokenRefresher();
                if (newToken && newToken !== this.token) {
                    feLog('info', 'sse_diag.proactive_refresh_ok', '');
                    this.token = newToken;
                    this.scheduleProactiveRefresh(newToken);
                    // Reconnect with the new token
                    if (this.connection) {
                        this.connection.close();
                        this.connection = null;
                    }
                    this.doConnect().catch(e => {
                        feLog('error', 'sse_diag.do_connect_failed', String(e), { source: 'proactive_refresh' });
                    });
                }
            } catch (e) {
                feLog('error', 'sse_diag.proactive_refresh_failed', String(e));
            }
        }, refreshIn);
    }

    // Initialize the single SSE connection
    connect(token) {
        if (!token) {
            feLog('warn', 'sse_diag.connect_no_token', '');
            return;
        }

        // If already connected with same token, do nothing
        if (this.connection && this.token === token && this.isConnected) {
            feLog('info', 'sse_diag.connect_already', '');
            return;
        }

        feLog('info', 'sse_diag.connect', '', {
            had_conn: String(!!this.connection),
            was_connected: String(this.isConnected),
        });

        // Store token for reconnection
        this.token = token;

        // Clear any existing reconnect timer
        this.clearReconnectTimer();

        // Close any existing connection
        this.disconnect();

        // Schedule proactive token refresh
        this.scheduleProactiveRefresh(token);

        this.doConnect();
    }
    
    // Handle successful connection
    handleConnectionOpen() {
        if (!this._perfMarked) {
            this._perfMarked = true;
            perfMark('sseConnected');
        }
        const elapsed = this._connectStartTime ? Date.now() - this._connectStartTime : -1;
        feLog('info', 'sse_diag.open', '', {
            elapsed_ms: String(elapsed),
            attempt: String(this.reconnectAttempts),
        });
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000; // Reset delay
        this.consecutiveQuickFailures = 0; // Reset quick-failure counter
        this.clearReconnectTimer();
        this.updateConnectionState(true);
        this.lastEventTime = Date.now(); // Reset last event time on new connection
        this.setupHeartbeatTimeout(); // Start heartbeat monitoring
        
        // Important: handlers are preserved across reconnections
        // The handlers Map is not cleared on disconnect, so they remain registered
        
        // After reconnect, we need to re-send the current tab to server
        if (this.activeTab && this.token) {
            // Import dynamically to avoid circular dependency
            import('./api').then(({ sendTabChange }) => {
                sendTabChange(this.token, this.activeTab).catch(err => {
                    console.error('[GlobalSSE] Failed to re-send tab after reconnect:', err);
                });
            });
        }
    }
    
    // Handle connection errors
    handleConnectionError(error) {
        console.error('[GlobalSSE] Connection failed:', error);
        const elapsed = this._connectStartTime ? Date.now() - this._connectStartTime : -1;
        feLog('error', 'sse_diag.error', String(error), {
            elapsed_ms: String(elapsed),
            attempts: String(this.reconnectAttempts),
            has_conn: String(!!this.connection),
            ready: String(this.connection?.readyState ?? 'none'),
            quick_failures: String(this.consecutiveQuickFailures),
        });
        this.updateConnectionState(false);

        // Track consecutive quick failures (connection lived < QUICK_FAILURE_WINDOW_MS)
        if (elapsed >= 0 && elapsed < QUICK_FAILURE_WINDOW_MS) {
            this.consecutiveQuickFailures++;
        } else {
            this.consecutiveQuickFailures = 0;
        }

        // If too many consecutive quick failures, likely an auth problem — stop retrying
        if (this.consecutiveQuickFailures >= QUICK_FAILURE_THRESHOLD) {
            feLog('error', 'sse_diag.too_many_quick_failures', '', {
                count: String(this.consecutiveQuickFailures),
            });
            // Clear proactive refresh timer before nulling token — otherwise the
            // timer fires later, tries to refresh with null token, and logs errors.
            if (this.proactiveRefreshTimer) {
                clearTimeout(this.proactiveRefreshTimer);
                this.proactiveRefreshTimer = null;
            }
            this.token = null;
            if (this.connection) {
                this.connection.close();
                this.connection = null;
            }
            this.connectionStateListeners.forEach(listener => {
                try { listener(false); } catch (e) { /* ignore */ }
            });
            return;
        }

        // Close the connection if it exists
        if (this.connection) {
            this.connection.close();
            this.connection = null;
        }

        // Schedule reconnection
        this.scheduleReconnect();
    }
    
    // Schedule a reconnection attempt
    scheduleReconnect() {
        // Don't schedule if we don't have a token
        if (!this.token) {
                return;
        }
        
        // Don't schedule if already scheduled
        if (this.reconnectTimer) {
            return;
        }
        
        this.reconnectAttempts++;
        
        // Calculate delay with exponential backoff
        const delay = Math.min(
            this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 5)),
            this.maxReconnectDelay
        );
        
        
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.doConnect().catch(e => {
                feLog('error', 'sse_diag.do_connect_failed', String(e), { source: 'reconnect' });
            });
        }, delay);
    }
    
    // Clear any pending reconnect timer
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    
    // Update connection state and notify listeners
    updateConnectionState(connected) {
        const wasConnected = this.isConnected;
        this.isConnected = connected;
        
        // Only notify if state actually changed
        if (wasConnected !== connected) {
            this.connectionStateListeners.forEach(listener => {
                try {
                    listener(connected);
                } catch (e) {
                    console.error('[GlobalSSE] Error in connection state listener:', e);
                }
            });
        }
    }

    // Handle incoming events and route to appropriate handler
    handleEvent(tab, eventType, data) {
        // Update last event time
        this.updateLastEventTime();
        
        const handler = this.handlers.get(tab);
        if (handler) {
            handler(eventType, data);
        }
    }

    // Register a handler for a specific tab
    registerHandler(tab, handler) {
        this.handlers.set(tab, handler);
    }

    // Unregister a handler
    unregisterHandler(tab) {
        this.handlers.delete(tab);
    }

    // Update the active tab (this will be sent with the SSE connection)
    setActiveTab(tab) {
        this.activeTab = tab;
        // Save to localStorage for persistence
        if (tab) {
            // Map server tab names back to UI tab names for storage
            const tabMapping = {
                'world_v3': 'world',
                'v3_inventory': 'inventory',
                'v3_hero': 'hero',
                'v3_guild': 'guild',
                'feed_v3': 'feed'
                // Market_Deals and Market_Offers are stored as-is
            };
            const uiTabName = tabMapping[tab] || tab;
            localStorage.setItem('v3_active_tab', uiTabName);
        }
        // Note: The actual tab change is communicated via sendTabChange API
    }

    // Disconnect the SSE connection
    disconnect() {
        this.clearReconnectTimer();
        this.clearHeartbeatTimeout();
        if (this.proactiveRefreshTimer) {
            clearTimeout(this.proactiveRefreshTimer);
            this.proactiveRefreshTimer = null;
        }
        if (this.connection) {
            this.connection.close();
            this.connection = null;
            this.updateConnectionState(false);
        }
    }

    // Get connection status
    getConnectionStatus() {
        // Check multiple conditions for true connection status
        if (!this.connection || this.connection.readyState !== EventSource.OPEN) {
            return false;
        }
        
        return this.isConnected;
    }
    
    // Add listener for connection state changes
    addConnectionStateListener(listener) {
        this.connectionStateListeners.add(listener);
        // Immediately notify of current state
        listener(this.isConnected);
    }
    
    // Remove connection state listener
    removeConnectionStateListener(listener) {
        this.connectionStateListeners.delete(listener);
    }
    
    // Internal method to do the actual connection
    async doConnect() {
        if (!this.token) {
            console.error('[GlobalSSE] Cannot connect without token');
            return;
        }

        // Check if token is expired before attempting connection
        if (isTokenExpired(this.token)) {
            feLog('warn', 'sse_diag.token_expired_before_connect', '');
            if (this.tokenRefresher) {
                try {
                    const newToken = await this.tokenRefresher();
                    if (newToken) {
                        this.token = newToken;
                        this.scheduleProactiveRefresh(newToken);
                        feLog('info', 'sse_diag.token_refreshed_before_connect', '');
                    } else {
                        feLog('error', 'sse_diag.token_refresh_returned_null', '');
                        return;
                    }
                } catch (e) {
                    feLog('error', 'sse_diag.token_refresh_failed', String(e));
                    return;
                }
            } else {
                feLog('error', 'sse_diag.token_expired_no_refresh', '');
                return;
            }
        }

        this._connectStartTime = Date.now();
        feLog('info', 'sse_diag.do_connect', '', {
            tab: this.activeTab || 'null',
            attempt: String(this.reconnectAttempts),
        });

        // Create handlers for all event types
        const updateHandlers = {
            // Heartbeat handler
            heartbeat: (data) => {
                this.updateLastEventTime();
            },
            
            // V3 World events
            v3Init: (data) => this.handleEvent('world_v3', 'v3Init', data),
            v3Patch: (data) => {
                // v3Patch contains profile updates that need to be available on ALL tabs
                // Route to world_v3 handler which updates the global v3State
                this.handleEvent('world_v3', 'v3Patch', data);
                // Also route to active tab so tab-specific handlers can process if needed
                if (this.activeTab && this.activeTab !== 'world_v3') {
                    this.handleEvent(this.activeTab, 'v3Patch', data);
                }
            },
            
            // V3 Inventory/Hero/Guild events
            v3Inventory: (data) => this.handleEvent('v3_inventory', 'v3Inventory', data),
            v3Hero: (data) => this.handleEvent('v3_hero', 'v3Hero', data),
            v3Guild: (data) => this.handleEvent('v3_guild', 'v3Guild', data),
            
            // Marketplace events - route directly to server tab names
            marketOffers: (data) => {
                this.handleEvent('Market_Offers', 'marketOffers', data);
            },
            marketQuotes: (data) => {
                this.handleEvent('Market_Offers', 'marketQuotes', data);
            },
            marketDeals: (data) => {
                this.handleEvent('Market_Deals', 'marketDeals', data);
            },
            
            // Unified v3Update event
            v3Update: (data) => {
                try {
                    const update = JSON.parse(data);
                    this.handleEvent(update.tab, 'v3Update', update);
                } catch (e) {
                    console.error('[GlobalSSE] Failed to parse v3Update:', e);
                }
            },
            
            // Default message handler for feed and other tabs
            onmessage: (event) => {
                // Update last event time for any message
                this.updateLastEventTime();
                
                try {
                    // Handle feed messages when on feed tab
                    if (this.activeTab === 'feed' || this.activeTab === 'feed_v3') {
                        const updates = JSON.parse(event.data);
                        if (Array.isArray(updates)) {
                            updates.forEach(({ key, value }) => {
                                if (/^F\d+$/.test(key)) {
                                    this.handleEvent('feed', '_widgetDynamic', { key, value });
                                }
                            });
                            
                            if (updates.length === 0) {
                                this.handleEvent('feed', '_clearWidgets', null);
                            }
                        }
                    }
                } catch (e) {
                    console.error('[GlobalSSE] Failed to parse message:', e);
                }
            }
        };
        
        // Connect with the initial tab (we'll update it via setActiveTab)
        this.connection = connectSSE(
            this.token,
            updateHandlers,
            (error) => {
                console.error('[GlobalSSE] Connection error:', error);
                this.handleConnectionError(error);
            },
            this.activeTab || localStorage.getItem('v3_active_tab') || 'world_v3' // Use saved tab or default to world_v3
        );

        if (this.connection) {
            // Monitor connection state
            this.connection.addEventListener('open', () => {
                this.handleConnectionOpen();
            });

            this.connection.addEventListener('error', (e) => {
                console.error('[GlobalSSE] Connection error event:', e);
                this.handleConnectionError(e);
            });

            // Probe SSE state after 5s to see if it connected/failed/stuck
            const connRef = this.connection;
            const startTime = this._connectStartTime;
            setTimeout(() => {
                feLog('info', 'sse_diag.probe_5s', '', {
                    ready: String(connRef?.readyState ?? 'gone'),
                    is_connected: String(this.isConnected),
                    same_conn: String(this.connection === connRef),
                    elapsed_ms: String(startTime ? Date.now() - startTime : -1),
                });
            }, 5000);
        }
    }
    
    // Setup heartbeat timeout
    setupHeartbeatTimeout() {
        // Clear any existing timeout
        this.clearHeartbeatTimeout();
        
        // Set a new timeout - if no keepalive/event in heartbeatInterval, reconnect
        this.heartbeatTimeout = setTimeout(() => {
            feLog('warn', 'sse_diag.heartbeat_timeout', '', {
                interval_s: String(this.heartbeatInterval / 1000),
                attempts: String(this.reconnectAttempts),
            });
            this.updateConnectionState(false);
            
            // Trigger reconnection
            if (this.connection) {
                this.connection.close();
                this.connection = null;
            }
            this.scheduleReconnect();
        }, this.heartbeatInterval);
    }
    
    // Clear heartbeat timeout
    clearHeartbeatTimeout() {
        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        }
    }
    
    // Update last event time whenever we receive any event
    updateLastEventTime() {
        this.lastEventTime = Date.now();
        
        // Reset heartbeat timeout on any event
        this.setupHeartbeatTimeout();
        
        // If we were marked as disconnected due to stale connection, update state
        if (!this.isConnected && this.connection && this.connection.readyState === EventSource.OPEN) {
            this.updateConnectionState(true);
        }
    }
    
    // Setup page visibility handling
    setupVisibilityHandling() {
        if (typeof document === 'undefined') return;
        
        // Use the modern visibility API with fallbacks
        const handleVisibilityChange = () => {
            const wasVisible = this.isPageVisible;
            
            // Check if page is visible using various methods
            if (document.hidden !== undefined) {
                this.isPageVisible = !document.hidden;
            } else if (document.webkitHidden !== undefined) {
                this.isPageVisible = !document.webkitHidden;
            } else if (document.msHidden !== undefined) {
                this.isPageVisible = !document.msHidden;
            }
            
            
            // If page became visible and we have a token but no connection, reconnect
            if (!wasVisible && this.isPageVisible && this.token && !this.isConnected) {
                this.clearReconnectTimer();
                this.reconnectAttempts = 0; // Reset attempts for immediate reconnect
                this.doConnect().catch(e => {
                    feLog('error', 'sse_diag.do_connect_failed', String(e), { source: 'visibility' });
                });
            }
        };
        
        // Add visibility change listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('webkitvisibilitychange', handleVisibilityChange);
        document.addEventListener('msvisibilitychange', handleVisibilityChange);
        
        // Also listen for focus/blur as backup
        window.addEventListener('focus', () => {
            if (!this.isPageVisible) {
                this.isPageVisible = true;
                handleVisibilityChange();
            }
        });
        
        window.addEventListener('blur', () => {
            // Don't immediately mark as hidden on blur, wait for visibility change
        });
        
        // Handle wake from sleep - detect if time gap is too large
        let lastActiveTime = Date.now();
        const checkWakeFromSleep = () => {
            const now = Date.now();
            const timeDiff = now - lastActiveTime;
            
            // If more than 5 seconds have passed, we likely woke from sleep
            if (timeDiff > 5000 && this.isPageVisible && this.token && !this.isConnected) {
                this.clearReconnectTimer();
                this.reconnectAttempts = 0;
                this.doConnect().catch(e => {
                    feLog('error', 'sse_diag.do_connect_failed', String(e), { source: 'wake' });
                });
            }
            
            lastActiveTime = now;
        };
        
        // Check every second when page is visible
        setInterval(() => {
            if (this.isPageVisible) {
                checkWakeFromSleep();
            }
        }, 1000);
    }
}

// Create singleton instance
const globalSSEService = new GlobalSSEService();

// Export to window for debugging and AppV3
if (typeof window !== 'undefined') {
    window.globalSSE = globalSSEService;
}

// Export singleton
export default globalSSEService;